export const getSelectStyles = (error) => ({
    control: (base, state) => ({
      ...base,
      borderColor: error
        ? "#dc3545" // red if error
        : state.isFocused
        ? "#86b7fe" // bootstrap focus blue
        : "#ced4da", // normal bootstrap border

      minHeight: "38px", // match bootstrap height
    }),
  });

  export const formatSeriesLabel = (value) => {
    if (!value) return "";

    const [year, month] = value.split("-");
    const date = new Date(year, month - 1);

    return date.toLocaleString("en-US", {
      month: "long",
      year: "numeric",
    });
  };

  export const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";

  export const normalizeRemarks = (value = "") => {
    return value
      .replace(/<p><br><\/p>/gi, "")
      .replace(/&nbsp;/gi, " ")
      .trim();
  };

  /* ── Helpers ─────────────────────────────────────────────────────── */
// Resolve relative file paths against onsite_url (same base used by interceptorServices)
export const toFullUrl = (url, onsite_url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  const base = (onsite_url || "http://localhost:8181").replace(/\/$/, "");
  return `${base}/${url.replace(/^\//, "")}`;
};

export const fmtSize = (b) =>
  !b ? "" : b < 1024 ? `${b}B` : b < 1048576 ? `${(b / 1024).toFixed(0)}KB` : `${(b / 1048576).toFixed(1)}MB`;


export const newId = () =>
  `SPN-${Math.random().toString(36).slice(2, 10).toUpperCase()}`;

export const newTransactionId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `TXN-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;


export const titles = ["Mr", "Mrs", "Ms", "Dr", "Prof"];
export const accommodationBenefitTypes = ["Default", "Sponsored", "Complimentary"];
export const accommodationTypes = ["Single Occupancy", "Double Occupancy"];
export const getSubdomain = () =>
  localStorage.getItem('sub_domain') || localStorage.getItem('cms_sub_domain') || '';

// utils/dateUtils.js

export const formatDate = (date) => {
  if (!date) return "-";

  const parsedDate = new Date(date);

  if (isNaN(parsedDate.getTime())) return "-";

  return parsedDate.toLocaleDateString("en-GB");
};


export const numberValue = (value) =>
  value === "" ? "" : Number(value);

export const roundMoney = (value) =>
  Math.round(Number(value || 0));

export const currencySymbol = (currency) =>
  currency === "EUR" ? "€" : "$";

export const money = (value, currency) =>
  `${currencySymbol(currency)}${Math.round(Number(value || 0))}`;

export const applySponsorshipPercentage = (
  amount,
  percentage
) =>
  Math.round(
    Number(amount || 0) *
      (1 - Number(percentage || 0) / 100) *
      100
  ) / 100;

export const priceRow = (
  priceSetup,
  typeId,
  currency
) => {
  const source = priceSetup.find(
    (item) =>
      Number(item.regtype_id) === Number(typeId)
  );

  const suffix = currency === "EUR" ? "EUR" : "USD";

  return {
    early_bird: source?.[`early_${suffix}`] ?? "",
    mid_term: source?.[`mid_${suffix}`] ?? "",
    final_call: source?.[`final_${suffix}`] ?? "",
  };
};


export const formatTierDate = (value) =>
  value ? new Date(value).toISOString().split("T")[0] : "Date not configured";


export const getActiveTier = (dates) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const earlyDate = dates?.conedate ? new Date(dates.conedate) : null;
  const midDate = dates?.ctwodate ? new Date(dates.ctwodate) : null;
  const finalDate = dates?.cthreedate ? new Date(dates.cthreedate) : null;
  if (earlyDate && today <= earlyDate) return "early";
  if (midDate && today <= midDate) return "mid";
  return finalDate ? "final" : "early";
};

export const formatCountdown = (dateValue, now, endOfDay = true) => {
  if (!dateValue) return "Date not configured";
  const target = new Date(
    dateValue.length === 10
      ? `${dateValue}T${endOfDay ? "23:59:59.999" : "00:00:00"}`
      : dateValue,
  );
  if (Number.isNaN(target.getTime())) return "Date not configured";
  const remaining = target.getTime() - now.getTime();
  if (remaining <= 0) return "Expired";
  const totalSeconds = Math.floor(remaining / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${days}d ${String(hours).padStart(2, "0")}h ${String(minutes).padStart(2, "0")}m ${String(seconds).padStart(2, "0")}s`;
};

export const getSponsorshipStatus = (startValue, endValue, now) => {
  if (!startValue) return "Not configured";
  const start = new Date(`${startValue}T00:00:00`);
  const end = endValue ? new Date(`${endValue}T23:59:59.999`) : null;
  if (Number.isNaN(start.getTime()) || (end && Number.isNaN(end.getTime()))) {
    return "Invalid dates";
  }
  if (end && end < start) return "Invalid dates";
  if (now < start) return "Upcoming";
  if (!end || now <= end) return "Active";
  return "Expired";
};


export const accommodationPrice = (addons, type, currency) => {
  const searchText = type.toLowerCase().replace(" occupancy", "");
  const addon = addons.find((item) =>
    String(item.regaddon_name || "")
      .toLowerCase()
      .includes(searchText),
  );
  return currency === "EUR"
    ? (addon?.regaddon_euro_price ?? "")
    : (addon?.regaddon_price ?? "");
};

export const normalizeAccommodation = (accommodation) => {
  const value = Array.isArray(accommodation) ? accommodation[0] : accommodation;
  const selectedType = value?.type || "Single Occupancy";
  const selectedQuantity = {
    number_of_nights: value?.number_of_nights ?? 1,
    number_of_rooms: value?.number_of_rooms ?? value?.qty ?? 1,
  };
  return {
    type: selectedType,
    actual_amount: value?.actual_amount ?? "",
    discount_percentage: value?.discount_percentage ?? 0,
    ...selectedQuantity,
    quantities: {
      "Single Occupancy": value?.quantities?.["Single Occupancy"] || { number_of_nights: 1, number_of_rooms: 1 },
      "Double Occupancy": value?.quantities?.["Double Occupancy"] || { number_of_nights: 1, number_of_rooms: 1 },
    },
    sponsor_id: value?.sponsor_id ?? "",
  };
};

export const getSponsorshipUrl = (row, fallbackSubDomain = "") => {
  if (row?.sponsorship_link) return row.sponsorship_link;

  const sponsorId = row?.sponsor_id || row?.sponser_id;
  const sponsorshipPath = `/registration/sponsorship/${sponsorId}`;
  const sponsorshipSubdomain = row?.sub_domain || fallbackSubDomain;

  if (!sponsorshipSubdomain || !window.location.hostname) {
    return sponsorshipPath;
  }

  return window.location.hostname === "localhost"
    ? `http://localhost:3022${sponsorshipPath}`
    : `https://${sponsorshipSubdomain}.scientexconference.com${sponsorshipPath}`;
};